/**
 * The active internal role, for the screens that must offer less to a lesser role.
 *
 * This is NOT authorization. Every command is authorized server-side against the authorization
 * matrix, and a viewer who types an owner's URL gets the backend's 403 rendered as the forbidden
 * state. What this decides is only what a surface OFFERS, which is a separate question and the
 * reason the reference's sidebar never grew a row for something most roles cannot use.
 *
 * The default is `null` — no role, offer nothing role-gated. The visual acceptance demo runs with
 * that default on purpose: it has no session at all, so a screen compared against the frozen
 * prototype is compared in the state the prototype itself is in. The product wraps the tree in
 * `V07RoleProvider` with the role the backend reported for the signed-in user.
 */
import { createContext, type ReactElement, type ReactNode, useContext } from 'react';

export type V07Role = 'owner' | 'admin' | 'operator' | 'viewer' | null;

/**
 * Who the shell's account block names, and what «Çıkış» does.
 *
 * The reference's own block reads «Türker Baş · Yönetici», and the demo keeps exactly that: it has
 * no session, and a capture compared against the frozen prototype must be compared in the state
 * the prototype is in. The product supplies the signed-in user and a real sign-out. The markup is
 * identical either way — only the words differ, and only where there is a session to read them
 * from.
 */
export interface V07Account {
  name: string;
  initials: string;
  roleLabel: string;
  signOut: (() => void) | null;
  /**
   * Who this is, for work a screen must keep across a reload and must not hand to anybody else.
   *
   * The internal user id the session already resolves — not a token, not a secret, and never sent
   * anywhere. A screen that remembers an unfinished act (`lib/pending-intent.ts`) binds it to this,
   * so another account on the same tab cannot resume it. `null` in the demo, which has no session
   * and therefore remembers nothing.
   */
  ownerKey: string | null;
}

const RoleContext = createContext<V07Role>(null);
const AccountContext = createContext<V07Account | null>(null);

export function V07RoleProvider({
  role,
  account,
  children,
}: {
  role: V07Role;
  account?: V07Account;
  children: ReactNode;
}): ReactElement {
  return (
    <RoleContext.Provider value={role}>
      <AccountContext.Provider value={account ?? null}>{children}</AccountContext.Provider>
    </RoleContext.Provider>
  );
}

export function useV07Role(): V07Role {
  return useContext(RoleContext);
}

export function useV07Account(): V07Account | null {
  return useContext(AccountContext);
}
